// Closed worlds on the Portal (Ben, Oct 19 2026: "allow a player to tap on a closed world to see their stats and who won";
// who won = the top 5 guilds and players). The game keeps nothing of a world once it ends — no winner, and its process is
// gone — so Valor Web saves, while each world runs, the player's own numbers and the world's top fives (Cloudflare KV,
// worker/history.ts), and Valor Tools' archive fills in the worlds it has and Valor Web doesn't. Pure functions, shared by the
// Worker and the app (tests/unit/history.test.ts).

/** One row of a final top five. `id` is the player's or the guild's game id (to mark "you"). */
export interface Standing {
  rank: number;
  id: number;
  name: string;
  /** a guild's tag; for a player, their guild's tag */
  tag?: string;
  points: number;
  cities: number;
  you?: boolean;
}

/** The player's own last numbers on a world (KV me:<uid>:<wid>). */
export interface MeRec {
  at: number;
  rank: number;
  points: number;
  cities: number;
  gid?: number;
  guild?: string;
  gtag?: string;
  guildRank?: number;
  ksa: number;
  ksd: number;
  kst: number;
  rankKsa?: number;
  rankKsd?: number;
  rankKst?: number;
}

/** A world's top five guilds and players (KV top:<wid>), shared by everyone on it. */
export interface TopRec {
  at: number;
  guilds: Standing[];
  players: Standing[];
}

/** The worlds Valor Web has seen the player in (KV idx:<uid>). `gone`: when it first stopped being one of theirs while not
 *  open to join either — the world ended. */
export interface IndexRec {
  at: number;
  /** the index's version: a newer Worker refreshes an older index at once (INDEX_V) */
  v?: number;
  /** gone: when Valor Web saw the world stop being theirs (0 = it had ended before Valor Web ever saw it — no date known) */
  worlds: Record<string, { name?: string; seen: number; gone?: number }>;
  /** worlds already looked for on the game's boards (late reads), and when */
  probes?: Record<string, number>;
}

/** Oct 19 2026 (Ben: "It is not showing worlds that have ended, I was in World 3 before it ended"): v2 also looks for worlds that
 *  ended before Valor Web saved anything — the game's world boards outlive the world (front-server reads them from its own
 *  tables), so your rank, points, cities, kill scores and the top five players can still be read. */
export const INDEX_V = 2;
/** a world looked for and not found is looked for again after a day */
export const PROBE_AGAIN_MS = 24 * 3600e3;
const MAX_PROBES = 12;
/** how far past the highest world id the game mentions to look */
const PROBE_PAST = 3;

export interface ClosedWorld {
  wid: number;
  name?: string;
  endedAt: number;
  savedAt: number;
  /** "web": Valor Web's own copy · "vt": Valor Tools' archive */
  source: "web" | "vt";
  me: Omit<MeRec, "at" | "gid">;
  guilds: Standing[];
  players: Standing[];
}

const num = (v: unknown): number => {
  const n = typeof v === "number" ? v : typeof v === "string" ? Number(v.replace(/,/g, "")) : NaN;
  return Number.isFinite(n) ? n : 0;
};
const opt = (v: unknown): number | undefined => (num(v) > 0 ? num(v) : undefined);
const str = (v: unknown, max = 40): string | undefined => (typeof v === "string" && v.trim() ? v.trim().slice(0, max) : undefined);
const ids = (v: unknown): number[] => (Array.isArray(v) ? v.map(num).filter((x) => x > 0 && x <= 100000) : []);

export const HOUR = 3600e3;
/** How often a player's own numbers and the world's top fives are read again. */
export const REFRESH_MS = HOUR;
/** At most this many worlds are kept per player (oldest-ended dropped first). */
export const MAX_WORLDS = 40;

/** /init's worlds → the updated index: their worlds now (seen), and the ones that ended (gone). A world they left that is
 *  still open to join isn't an ended one — it's dropped. Answers the index and the worlds they're in. */
export function markWorlds(prev: IndexRec | null, world: unknown, names: Record<string, unknown> | undefined, now: number): { idx: IndexRec; inW: number[]; dropped: number[] } {
  const w = (world && typeof world === "object" ? world : {}) as Record<string, unknown>;
  const inW = ids(w.worlds_in), notIn = ids(w.worlds_not_in), ended = ids(w.worlds_ended);
  const worlds: IndexRec["worlds"] = { ...(prev?.worlds ?? {}) };
  const dropped: number[] = [];
  for (const wid of inW) worlds[wid] = { name: str(names?.[String(wid)]) ?? worlds[wid]?.name, seen: now };
  for (const k of Object.keys(worlds)) {
    const wid = Number(k);
    if (inW.includes(wid)) continue;
    if (notIn.includes(wid) && !ended.includes(wid)) {
      delete worlds[k];
      dropped.push(wid);
      continue;
    }
    worlds[k] = { ...worlds[k], name: worlds[k].name ?? str(names?.[k]), gone: worlds[k].gone ?? now };
  }
  // keep the newest MAX_WORLDS
  const keys = Object.keys(worlds).sort((a, b) => (worlds[b].gone ?? Infinity) - (worlds[a].gone ?? Infinity));
  for (const k of keys.slice(MAX_WORLDS)) {
    delete worlds[k];
    dropped.push(Number(k));
  }
  return { idx: { at: now, v: INDEX_V, worlds, probes: prev?.probes }, inW, dropped };
}

/** The worlds to look for on the game's boards: the game's ended ones and every world id up to a few past the highest known
 *  (a world that is over may no longer be listed at all) that is neither theirs now nor open to join, which Valor Web has no
 *  copy of (`have`) and hasn't looked for in the last day. */
export function probeCandidates(idx: IndexRec, world: unknown, have: number[], now: number): number[] {
  const w = (world && typeof world === "object" ? world : {}) as Record<string, unknown>;
  const inW = ids(w.worlds_in), notIn = ids(w.worlds_not_in), ended = ids(w.worlds_ended);
  const known = [...inW, ...notIn, ...ended, ...Object.keys(idx.worlds).map(Number)];
  const top = Math.min(60, Math.max(0, ...known) + PROBE_PAST);
  const out: number[] = [];
  for (let wid = 1; wid <= top; wid++) {
    if (inW.includes(wid) || (notIn.includes(wid) && !ended.includes(wid)) || have.includes(wid)) continue;
    const at = idx.probes?.[String(wid)];
    if (at && now - at < PROBE_AGAIN_MS) continue;
    out.push(wid);
  }
  for (const wid of ended) if (!out.includes(wid) && !have.includes(wid) && !(idx.probes?.[String(wid)] && now - idx.probes[String(wid)] < PROBE_AGAIN_MS)) out.push(wid);
  return out.slice(0, MAX_PROBES);
}

/** Your row on a board read with page_num 0 (the page you're on), or nothing. */
export function ownRow(raw: unknown, id: number): { rank: number; score: number; cities: number; name?: string } | null {
  const o = (raw as { output?: { rank?: unknown; results?: unknown[] } })?.output;
  const rows = (o?.results ?? []) as Record<string, unknown>[];
  const r = rows.find((x) => num(x.user_id ?? x.member) === id);
  const rank = num(o?.rank) || (r ? num(r.rank) : 0);
  if (!rank && !r) return null;
  const info = (r?.user_info ?? {}) as Record<string, unknown>;
  return { rank, score: r ? num(r.score) : 0, cities: num(info.num_villages), name: str(info.gname) ?? str(info.login) };
}

/** Your numbers on an ended world from its boards alone (no city to read any more): rank, points and cities from the points
 *  board, the kill scores and their ranks from theirs, the guild's name and rank from the guild board when it answers. */
export function meFromBoards(uid: number, b: { score: unknown; ksa?: unknown; ksd?: unknown; kst?: unknown; guild?: unknown }, now: number): MeRec | null {
  const p = ownRow(b.score, uid);
  if (!p || !p.rank) return null;
  const k = (raw: unknown) => ownRow(raw, uid);
  const a = k(b.ksa), d = k(b.ksd), t = k(b.kst);
  const go = (b.guild as { output?: { rank?: unknown; results?: unknown[] } })?.output;
  const gRank = num(go?.rank);
  const gRow = gRank ? ((go?.results ?? []) as Record<string, unknown>[]).find((x) => num(x.rank) === gRank) : undefined;
  const gInfo = (gRow?.user_info ?? {}) as Record<string, unknown>;
  return {
    at: now, rank: p.rank, points: p.score, cities: p.cities,
    gid: gRow ? opt(gRow.user_id ?? gRow.member) : undefined, guild: gRow ? str(gInfo.gname) : undefined, guildRank: gRank || undefined,
    ksa: a?.score ?? 0, ksd: d?.score ?? 0, kst: t?.score ?? 0, rankKsa: a?.rank || undefined, rankKsd: d?.rank || undefined, rankKst: t?.rank || undefined,
  };
}

/** The player's numbers from one village/get (player_stats, their city list, their guild) and the kill-score boards' ranks. */
export function meFrom(v: unknown, uid: number, ranks: { ksa?: number; ksd?: number; kst?: number }, now: number): MeRec | null {
  if (!v || typeof v !== "object") return null;
  const o = v as Record<string, unknown>;
  const ps = (o.player_stats ?? {}) as Record<string, unknown>;
  const pd = (o.player_details ?? {}) as Record<string, { target_char?: { villages?: unknown[] } } | undefined>;
  const tc = pd[String(uid)]?.target_char;
  const overviews = o.new_overviews && typeof o.new_overviews === "object" ? Object.keys(o.new_overviews as object).length : 0;
  const cities = Array.isArray(tc?.villages) ? tc!.villages!.length : overviews;
  if (!num(ps.total_points) && !cities) return null;
  const gi = (o.guild_info ?? {}) as Record<string, unknown>;
  const inGuild = Array.isArray(gi.members) && typeof gi.name === "string";
  let guildRank: number | undefined;
  if (inGuild && Array.isArray(gi.guild_details)) for (const row of gi.guild_details as Record<string, unknown>[]) if (row && "Rank" in row) guildRank = opt(row.Rank);
  return {
    at: now,
    rank: num(ps.rank),
    points: num(ps.total_points),
    cities,
    gid: inGuild ? opt(o.char_gid) ?? opt(gi.id) : undefined,
    guild: inGuild ? str(gi.name) : undefined,
    gtag: inGuild ? str(gi.tag, 8) : undefined,
    guildRank,
    ksa: num(ps.ksa),
    ksd: num(ps.ksd),
    kst: num(ps.kst),
    rankKsa: ranks.ksa || undefined,
    rankKsd: ranks.ksd || undefined,
    rankKst: ranks.kst || undefined,
  };
}

/** The first five rows of a rankings/player page (lb_info score). Players carry user_info.login, guilds user_info.gname. */
export function topRows(raw: unknown, guild: boolean): Standing[] {
  const out = ((raw as { output?: { results?: unknown[] } })?.output?.results ?? []) as Record<string, unknown>[];
  return out.slice(0, 5).map((r, i) => {
    const info = (r.user_info ?? {}) as Record<string, unknown>;
    return {
      rank: num(r.rank) || i + 1,
      id: num(r.user_id ?? r.member),
      name: (guild ? str(info.gname) : str(info.login)) ?? (guild ? "Unnamed guild" : "Unknown player"),
      points: num(r.score),
      cities: num(info.num_villages),
    };
  });
}

/** The rank a board gives you (`output.rank`), or nothing. */
export const myRank = (raw: unknown): number => num((raw as { output?: { rank?: unknown } })?.output?.rank);

const marked = (rows: Standing[], you: number | undefined): Standing[] => rows.slice(0, 5).map((r) => ({ ...r, you: !!you && r.id === you }));

/** The closed worlds to show: the ended ones in the index with a saved copy of the player's numbers, newest first. */
export function closedFrom(idx: IndexRec | null, uid: number, me: Record<string, MeRec | null>, top: Record<string, TopRec | null>): ClosedWorld[] {
  const out: ClosedWorld[] = [];
  for (const [k, w] of Object.entries(idx?.worlds ?? {})) {
    if (w.gone === undefined) continue;
    const m = me[k];
    if (!m) continue;
    const t = top[k];
    const { at, gid, ...mine } = m;
    out.push({
      wid: Number(k), name: w.name, endedAt: w.gone ?? 0, savedAt: Math.max(at, t?.at ?? 0), source: "web", me: mine,
      guilds: marked(t?.guilds ?? [], gid), players: marked(t?.players ?? [], uid),
    });
  }
  return out.sort((a, b) => b.endedAt - a.endedAt);
}

const cleanStanding = (x: unknown, i: number): Standing | null => {
  if (!x || typeof x !== "object") return null;
  const o = x as Record<string, unknown>;
  const name = str(o.name);
  if (!name) return null;
  return { rank: num(o.rank) || i + 1, id: num(o.id), name, tag: str(o.tag, 8), points: num(o.points), cities: num(o.cities), you: o.you === true };
};

/** One world from Valor Tools' archive (POST /vweb/history), checked field by field. */
export function cleanVtWorld(x: unknown): ClosedWorld | null {
  if (!x || typeof x !== "object") return null;
  const o = x as Record<string, unknown>;
  const wid = num(o.wid);
  if (!(wid > 0 && wid <= 100000)) return null;
  const m = (o.me ?? {}) as Record<string, unknown>;
  const list = (v: unknown) => (Array.isArray(v) ? v.slice(0, 5).map(cleanStanding).filter((s): s is Standing => !!s) : []);
  const me: ClosedWorld["me"] = {
    rank: num(m.rank), points: num(m.points), cities: num(m.cities), guild: str(m.guild), gtag: str(m.gtag, 8), guildRank: opt(m.guildRank),
    ksa: num(m.ksa), ksd: num(m.ksd), kst: num(m.kst), rankKsa: opt(m.rankKsa), rankKsd: opt(m.rankKsd), rankKst: opt(m.rankKst),
  };
  const guilds = list(o.guilds), players = list(o.players);
  if (!me.points && !me.rank && !guilds.length && !players.length) return null;
  return { wid, name: str(o.name), endedAt: num(o.endedAt), savedAt: num(o.savedAt), source: "vt", me, guilds, players };
}

/** Valor Web's own worlds, then Valor Tools' for the rest (and its top fives where Valor Web has none). Worlds the player is
 *  still in never show, even if Valor Tools thinks they ended. */
export function mergeHistory(web: ClosedWorld[], vt: ClosedWorld[], activeWids: number[]): ClosedWorld[] {
  const out = web.map((w) => {
    const v = vt.find((x) => x.wid === w.wid);
    if (!v) return w;
    return { ...w, guilds: w.guilds.length ? w.guilds : v.guilds, players: w.players.length ? w.players : v.players };
  });
  for (const v of vt) if (!out.some((w) => w.wid === v.wid) && !activeWids.includes(v.wid)) out.push(v);
  return out.sort((a, b) => b.endedAt - a.endedAt);
}

/** "You won": your guild finished first, or you did. */
export const wonWorld = (w: ClosedWorld): boolean => !!(w.guilds[0]?.you || w.players[0]?.you);
